import { cache as memoizedCache } from 'react';
import { Locale } from '@headless-workspace/config';
import { Result, ResultSuccess, unwrapResultSuccess } from '@headless-workspace/core-domain';
import { CommonDI, ProductsRepositoryType } from '@headless-workspace/domain/common/DI';
import { DataLayerClassName } from '@headless-workspace/domain/common/client';
import {
    EcomConfigKeys,
    getCachedEcomConfigByKey,
    getCachedVipBrands,
    HTMLString,
    TileProductAvailabilityValue,
    VipBrandsValue,
} from '@headless-workspace/domain/common/server';
import { Image } from '@headless-workspace/typings';
import { PredicateHelpers } from '@headless-workspace/utils';
import { DI } from '../../../DI';
import { HomeSlotsRepositoryType } from '../../../DI/dependencyInjection';
import { CTA } from '../types';
import { HomeSlotType } from './HomeSlotType';
import { HomeSlotBase, HomeSlotValue } from './HomeSlotsValue';
import { HomeBeautyTipsValue } from './beautyTips';
import { HomeCarouselValue } from './carousel';

export type HomeProductsRecommendationsValue = HomeSlotBase & {
    type: HomeSlotType.ProductsList | HomeSlotType.ProductRecommendations;
    title: HTMLString;
    cta: CTA | undefined;
    products: TileProductAvailabilityValue[];
};

export type HomeBeautyTipsProductsValue = HomeSlotBase & {
    type: HomeSlotType.BeautyTips;
    subTitle: string;
    contentId: string;
    title: HTMLString;
    image: Image;
    cta: CTA;
    products: TileProductAvailabilityValue[];
};

type ExcludedSlots = HomeCarouselValue | HomeBeautyTipsValue;

export type ReconciledHomeSlotsValue = Exclude<
    HomeSlotValue | HomeProductsRecommendationsValue | HomeBeautyTipsProductsValue,
    ExcludedSlots
>[];

async function fetchProducts(
    locale: Locale,
    productsIds: string[],
    vipBrands: VipBrandsValue,
    promoText: string | undefined,
): Promise<TileProductAvailabilityValue[]> {
    const productsResult = await CommonDI.get(ProductsRepositoryType).getProducts({
        locale,
        ids: productsIds,
        vipBrands,
        originalPriceText: promoText,
        isPreviewable: true,
    });
    return productsResult.type === 'success' ? (productsResult.data ?? []) : [];
}

function mapBeautyTipsSlot(
    slot: HomeBeautyTipsValue,
    products: TileProductAvailabilityValue[],
): HomeBeautyTipsProductsValue {
    return {
        products: products.map((product) => ({
            ...product,
            dataLayer: {
                id: `${slot.contentId}_product-${product.id}`,
                className: DataLayerClassName.beautyTips,
            },
        })),
        ...slot,
    };
}

async function processSlot(
    slot: HomeSlotValue,
    locale: Locale,
    vipBrands: VipBrandsValue,
    promoText: string | undefined,
    productListCarouselsIds: string[],
): Promise<ReconciledHomeSlotsValue[number] | null> {
    if (slot.type === HomeSlotType.ProductRecommendations) {
        const products = await fetchProducts(locale, (slot as HomeCarouselValue).productsIds, vipBrands, promoText);
        return {
            cta: slot.cta,
            products,
            ...slot,
        };
    }

    if (slot.type === HomeSlotType.BeautyTips) {
        const products = await fetchProducts(locale, (slot as HomeBeautyTipsValue).productsIds, vipBrands, promoText);
        return mapBeautyTipsSlot(slot as HomeBeautyTipsValue, products);
    }

    if (slot.type === HomeSlotType.TargetedCarousel) {
        return {
            ...slot,
            homeSlotCarouselIds: productListCarouselsIds,
        };
    }

    return slot as Exclude<HomeSlotValue, ExcludedSlots>;
}

async function reconcileHomeSlots(locale: Locale): Promise<Result<ReconciledHomeSlotsValue> | undefined> {
    const homeSlotsResult = await DI.get(HomeSlotsRepositoryType).getSlots({
        locale,
    });
    const homeSlots = unwrapResultSuccess(homeSlotsResult);

    if (!homeSlots) {
        return undefined;
    }

    const vipBrandsResult = await getCachedVipBrands(locale);
    const vipBrands = unwrapResultSuccess(vipBrandsResult) ?? {};

    const promoTextResult = await getCachedEcomConfigByKey(locale, EcomConfigKeys.PromoStandardText);
    const promoText = unwrapResultSuccess(promoTextResult);

    const productListCarouselsIds = homeSlots
        .filter((slot) => slot.type === HomeSlotType.TargetedCarousel)
        .map((slot) => slot.id);

    const processedSlots = await Promise.all(
        homeSlots.map((slot) =>
            processSlot(slot, locale, vipBrands, promoText?.originalPriceText, productListCarouselsIds),
        ),
    );

    return ResultSuccess(processedSlots.filter(PredicateHelpers.notNullOrUndefined));
}

export const getMemoHomeSlots = memoizedCache(reconcileHomeSlots);
